/**
 * The stop page — `docs/activity.md` §10.3, Board 14 A.
 *
 * What a person standing in a driveway needs, top to bottom: *‹ Thursday ·
 * ⋯*; the tags and *in 19 min* (*now* during it, *<n> min ago* after); the
 * place's name; *10:00–11:30 AM · 1 h 30 min*; the address and the drive
 * from the last stop; an 80 px map; **Navigate · Call · Text**, 62 px and
 * equal, reachable with a thumb; **Scope it** — *Voice note · Photo · Video ·
 * Note*, saved to the job; *What they want*; the person, their number, how
 * they like to be reached and the gate; **Latest**, the newest message, with
 * **All**; and **Open J-3318** · **Done**.
 *
 * A meeting without a job drops *What they want* and *Open …*; a remote one
 * drops the map and Navigate. Everything on it is the server's — the drive,
 * the minutes to the start — read through `GET /activity/stops/:id`.
 */
import { useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import {
  clockRange, milesWords, minutesWords, weekdayLong, zonedInstant, type NavApp, type RecordRef,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { usePhone } from '../../lib/use-narrow.js'
import { openNewRequest } from '../../requests/open-request.js'
import { Avatar } from '../../components/primitives.js'
import { ActivityContext } from '../activity-context.js'
import { activityRequest } from '../api.js'
import { Glyph, type GlyphName } from '../glyphs.js'
import { MoveSheet, WhichSheet } from '../calendar/actions.js'
import { openItemSheet } from '../calendar/overlays.js'
import { announceMoved } from '../calendar/tell.js'
import { ACTIVITY_MADE_EVENT } from '../new/new-sheets.js'
import type { SaveChip } from '../scope/saving-to.js'
import { useScopeUpload } from '../scope/use-scope-upload.js'
import { uploadQueue } from '../scope/upload-queue.js'
import { PhotosScreen } from '../scope/photos.js'
import { NoteSheet } from '../scope/note-sheet.js'
import { useActivityNav } from '../use-activity-history.js'
import { LogCallSheet, useCallReturn } from './log-call.js'
import { NavigateSheet, openNav, type Destination } from './navigate.js'
import { StopSheet } from './sheet.js'
import { StopMap } from './stop-map.js'
import { VoiceNoteSheet, type VoiceLink } from './voice-note.js'
import { phoneWords, whenWords, type StopPageCard } from './types.js'
import { markDone, offersDone } from '../calendar/done.js'

/** The page's layer: above Activity, below its own sheets. */
const PAGE_LAYER = 4

/** *Latest*'s bubble: the teal the boards give a text. */
const LATEST_TEAL = 'rgb(var(--status-job-approved-fg))'

/**
 * `navApp` (§10.3): the Activity page's remembered state when the stop page
 * is opened from it, else the same state asked for directly — it is per
 * device on the server either way (§4.6).
 */
function useNavApp(): { read: () => Promise<NavApp | null>; remember: (app: NavApp) => void } {
  const activity = useContext(ActivityContext)
  const read = useCallback(async () => {
    if (activity) return activity.state.navApp ?? null
    const answer = await activityRequest<{ state: { navApp?: NavApp } }>('/activity/state').catch(() => null)
    return answer?.state.navApp ?? null
  }, [activity])
  /* Sent at once: the next thing Navigate does is hand the phone to the maps app (§10.3). */
  const remember = useCallback((app: NavApp) => {
    if (activity) activity.change({ navApp: app }, { now: true })
    else void activityRequest('/activity/state', { method: 'PATCH', body: { navApp: app }, keepalive: true }).catch(() => {})
  }, [activity])
  return { read, remember }
}

function Section({ title, right, children }: { title: string; right?: ReactNode; children: ReactNode }) {
  return (
    <>
      <div className="mb-2 mt-4 flex items-center text-[11px] font-semibold uppercase tracking-[.06em] text-text-faint">
        {title}<span className="flex-1" />{right}
      </div>
      {children}
    </>
  )
}

function BigButton({ glyph, label, primary = false, onClick, href, disabled }: {
  glyph: GlyphName; label: string; primary?: boolean; onClick?: () => void; href?: string; disabled?: boolean
}) {
  const cls = cn(
    'flex h-[62px] flex-1 basis-0 flex-col items-center justify-center gap-1 rounded-[14px] text-[13px] font-semibold',
    /* A border on all three, so `basis-0` shares the row equally (Board 14 A: three equal buttons). */
    primary ? 'border border-accent bg-accent text-white' : 'border border-line bg-surface text-text',
    disabled && 'pointer-events-none opacity-50',
  )
  const body = (
    <>
      <span className={cn('inline-flex', primary ? 'text-white' : 'text-accent')}><Glyph name={glyph} size={21} stroke={2.1} /></span>
      {label}
    </>
  )
  return href && !disabled
    ? <a href={href} onClick={onClick} className={cls}>{body}</a>
    : <button type="button" onClick={onClick} disabled={disabled} className={cls}>{body}</button>
}

function ScopeTile({ glyph, label, sub, onClick }: { glyph: GlyphName; label: string; sub: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className="flex flex-1 basis-0 flex-col items-center gap-[5px] rounded-[14px] border border-accent-line bg-green-band-bg px-1 pb-2.5 pt-3">
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-accent-line bg-surface text-accent">
        <Glyph name={glyph} size={20} stroke={2.1} />
      </span>
      <b className="text-[12.5px] text-accent-dark">{label}</b>
      <span className="text-[10.5px] text-text-muted">{sub}</span>
    </button>
  )
}

/** *9:32 AM*, on this phone's clock. */
const timeOf = (iso: string) =>
  new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(new Date(iso))

export function StopPage({ id, onClose }: { id: string; onClose: () => void }) {
  /* Whatever a closed app left in the upload queue goes up while the stop is open (§11.4). */
  useEffect(() => { uploadQueue() }, [])
  const navigate = useNavigate()
  const phone = usePhone()
  const [card, setCard] = useState<StopPageCard | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let live = true
    /* A repeating meeting's occurrence comes as its calendar key, `<id>@<its own day>` (Aaron, Oct 24, small Q3). */
    const [activityId, occurrence] = id.split('@')
    activityRequest<StopPageCard>(`/activity/stops/${activityId}${occurrence ? `?occurrence=${occurrence}` : ''}`)
      .then((answer) => { if (live) setCard(answer) })
      .catch((cause: unknown) => { if (live) setFailed(cause instanceof Error ? cause.message : 'That stop could not be opened.') })
    return () => { live = false }
  }, [id, tick])

  if (!card) {
    return createPortal(
      <div className="fixed inset-0 z-[60] flex flex-col bg-surface" data-overlay-layer={PAGE_LAYER}>
        <div className="flex h-[52px] shrink-0 items-center border-b border-line px-3">
          <button type="button" onClick={onClose} className="inline-flex items-center gap-0.5 text-[15px] font-medium text-accent">
            <Glyph name="chevronLeft" size={20} />Back
          </button>
        </div>
        <p className="p-4 text-[13px] text-text-muted">{failed ?? 'Loading…'}</p>
      </div>,
      document.body,
    )
  }
  return <StopBody card={card} onClose={onClose} onChanged={() => setTick((n) => n + 1)} navigate={navigate} phone={phone} />
}

function StopBody({ card, onClose, onChanged, navigate, phone }: {
  card: StopPageCard
  onClose: () => void
  onChanged: () => void
  navigate: ReturnType<typeof useNavigate>
  phone: boolean
}) {
  const nav = useNavApp()
  const history = useActivityNav()
  const call = useCallReturn()
  const [screen, setScreen] = useState<'voice' | 'photos' | 'note' | null>(null)
  /* *Edit* and *Reschedule* on a repeating meeting ask first, then — for this one — its own card (phone note 4). */
  const [sheet, setSheet] = useState<'navigate' | 'more' | 'which' | 'move' | null>(null)
  const [asking, setAsking] = useState<'edit' | 'reschedule'>('edit')
  const timeZone = useContext(ActivityContext)?.me.user.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
  const [knownApp, setKnownApp] = useState<NavApp | null>(null)
  const [busy, setBusy] = useState(false)
  const photoInput = useRef<HTMLInputElement>(null)
  const videoInput = useRef<HTMLInputElement>(null)

  /* Where Scope it saves: the job, else the place, else the person (§10.3). */
  const target: RecordRef | null = card.job ? { type: 'job', id: card.job.id }
    : card.propertyId ? { type: 'property', id: card.propertyId }
      : card.contact ? { type: 'contact', id: card.contact.id } : null
  const targetLabel = card.job?.reference ?? card.propertyName ?? card.contact?.name ?? null
  const scope = useScopeUpload({
    record: target ?? { type: 'contact', id: card.id },
    propertyId: card.propertyId,
    orgUnitId: card.orgUnitId,
    stopId: card.id,
  })

  /* Board 15: *Saving to* names the job and the place; the person is linked quietly. */
  const chips: SaveChip[] = []
  if (card.job) chips.push({ type: 'job', id: card.job.id, label: card.job.reference })
  if (card.propertyId && card.propertyName) chips.push({ type: 'property', id: card.propertyId, label: card.propertyName })
  if (chips.length === 0 && card.contact) chips.push({ type: 'contact', id: card.contact.id, label: card.contact.name })
  const refs: RecordRef[] = [
    ...(card.job ? [{ type: 'job' as const, id: card.job.id }] : []),
    ...(card.propertyId ? [{ type: 'property' as const, id: card.propertyId }] : []),
    ...(card.contact ? [{ type: 'contact' as const, id: card.contact.id }] : []),
  ]
  const voiceLinks: VoiceLink[] = [
    ...(card.job ? [{ jobId: card.job.id, label: card.job.reference }] : []),
    ...(card.propertyId ? [{ propertyId: card.propertyId, ...(card.propertyName ? { label: card.propertyName } : {}) }] : []),
    ...(card.contact ? [{ contactId: card.contact.id, ...(refs.length === 1 ? { label: card.contact.name } : {}) }] : []),
  ]

  const destination: Destination = {
    latitude: card.latitude, longitude: card.longitude,
    address: [card.line1, card.city, card.state, card.postalCode].filter(Boolean).join(', ') || null,
  }
  const street = card.line1 ?? card.title
  const placeLine = [[card.city, card.state].filter(Boolean).join(', '), card.postalCode].filter(Boolean).join(' ')

  const goNavigate = async () => {
    if (!phone) { openNav('google', destination, false); return }
    const app = knownApp ?? await nav.read()
    if (app) openNav(app, destination, true)
    else setSheet('navigate')
  }

  /*
   * The conversation opens over the stop page rather than in its place (Aaron, Oct 26): back from
   * the thread comes back here, and back again to the calendar.
   */
  const openThread = (channel: 'sms' | 'email') => {
    if (card.conversationId) history.push({ tab: 'inbox', c: card.conversationId }, { fresh: true })
    else if (card.contact) history.push({ tab: 'inbox', contact: card.contact.id, channel }, { fresh: true })
  }

  /*
   * *Edit* (Aaron, Oct 25, phone note 4: "I don't seem to be able to edit
   * meetings on the calendar. At least in the three dot menu i should be able
   * to enter an edit mode") and *Reschedule*: the meeting's own sheet in edit
   * mode — every field, as the desktop's double-click and ⋯ › Edit open it —
   * or a site visit's booking (folder 48's book mode). A repeating meeting
   * asks *Just this one · Every one* first, as every move of one does; *Edit*
   * › *Just this one* is the same sheet for that occurrence alone (Aaron,
   * Oct 27, answer 12: "it should be able to change pretty much anything").
   */
  const meeting = card.kind === 'meeting'
  const openOwnSheet = (occurrence: string | null = null) => {
    setSheet(null)
    onClose()
    if (card.kind === 'appointment' && card.job) openNewRequest({ book: { jobId: card.job.id, appointmentId: card.id } })
    else if (meeting) openItemSheet({ id: card.id, kind: 'meeting', jobId: card.job?.id ?? null }, card.job?.id ?? null, occurrence)
    else navigate(`/activity?tab=calendar&date=${card.date}`)
  }
  const change = (verb: 'edit' | 'reschedule') => {
    if (meeting && card.recurrence) { setAsking(verb); setSheet('which'); return }
    openOwnSheet()
  }
  /* *Reschedule › Just this one*: that occurrence's day, start and length, as the calendar's own card moves it (0141). */
  const moveOne = async (place: { date: string; start: number; minutes: number }) => {
    await activityRequest(`/activity/occurrences/${card.id}/move`, { method: 'POST', body: {
      occurrence: card.occurrence ?? card.date, which: 'one',
      startsAt: zonedInstant(place.date, place.start, timeZone).toISOString(), minutes: place.minutes,
    } })
    window.dispatchEvent(new Event(ACTIVITY_MADE_EVENT))
    announceMoved({ id: card.id, kind: 'meeting', occurrence: card.occurrence ?? card.date })
    onChanged()
  }

  const done = async () => {
    setBusy(true)
    /* That occurrence alone, for a repeating meeting (0152). */
    await markDone(card).catch(() => {})
    setBusy(false)
    onChanged()
    onClose()
  }

  const pick = async (files: FileList | null, kind: 'photo' | 'video') => {
    if (!files || files.length === 0) return
    const taken = await scope.add(files, kind)
    if (photoInput.current) photoInput.current.value = ''
    if (videoInput.current) videoInput.current.value = ''
    if (taken > 0) setScreen('photos')
  }

  const when = whenWords(card)
  const contact = card.contact
  const phoneNumber = contact?.phone ?? null
  const contactLine = contact ? [
    contact.phoneKind,
    contact.prefers ? `prefers ${contact.prefers === 'sms' ? 'Text' : 'Email'}` : null,
    card.access,
  ].filter(Boolean).join(' · ') : ''
  const showMap = !card.remote && card.latitude !== null && card.longitude !== null
  const canText = Boolean(contact && (card.conversationId || (phoneNumber && !contact.optedOut) || contact.email))

  return createPortal(
    <div className="fixed inset-0 z-[60] flex flex-col bg-surface [line-height:normal]" data-overlay-layer={PAGE_LAYER}
      role="dialog" aria-modal="true" aria-label={card.title}>
      <div className="flex h-[calc(53px+env(safe-area-inset-top))] shrink-0 items-center gap-2 border-b border-line bg-surface px-3 pt-[env(safe-area-inset-top)]">
        <button type="button" onClick={onClose}
          className="inline-flex h-11 items-center gap-0.5 text-[15px] font-medium text-accent">
          <Glyph name="chevronLeft" size={20} />{weekdayLong(card.date)}
        </button>
        <span className="flex-1" />
        {/* A finger's 44 px around the 20 px glyph — it is the way to Edit (phone note 4). */}
        <button type="button" aria-label="More" aria-haspopup="dialog" onClick={() => setSheet('more')}
          className="-mr-2.5 inline-flex h-11 w-11 items-center justify-center text-text-muted">
          <Glyph name="more" size={20} />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto bg-surface px-4 py-3.5">
        <div className="flex items-center gap-1.5">
          {card.tags.map((tag) => (
            <span key={tag} className="inline-flex h-5 items-center gap-[3px] whitespace-nowrap rounded px-1.5 text-[11px] font-semibold bg-accent-soft text-accent-dark">
              {tag}
            </span>
          ))}
          <span className="flex-1" />
          {when && (
            <span className={cn('text-[12px] font-semibold', when.endsWith('ago') ? 'text-text-muted' : 'text-accent-dark')}>{when}</span>
          )}
        </div>
        <b className="mt-2 block text-[20px] leading-[1.25]">{card.title}</b>
        <div className="mt-0.5 text-[13.5px] text-text-muted">
          {clockRange(card.start, card.end)} · {minutesWords(card.end - card.start)}
        </div>
        {!card.remote && card.line1 && (
          <div className="mt-2 flex items-center gap-1.5 text-[14px] font-semibold">
            <span className="inline-flex text-accent"><Glyph name="pin" size={15} stroke={2.2} /></span>
            {[card.line1, card.city, card.state].filter(Boolean).join(', ')}
          </div>
        )}
        {!card.remote && card.driveIn && (
          <div className="ml-[21px] mt-[3px] flex items-center gap-[5px] text-[12.5px] text-text-muted">
            <Glyph name="car" size={12} stroke={2.2} />
            {card.driveIn.minutes} min from {card.driveIn.from} · {milesWords(card.driveIn.miles)}
          </div>
        )}
        {showMap && (
          <>
            <div className="h-2.5" />
            <StopMap latitude={card.latitude!} longitude={card.longitude!} />
          </>
        )}
        <div className="mt-3 flex gap-2">
          {!card.remote && <BigButton glyph="navigate" label="Navigate" primary onClick={() => void goNavigate()} />}
          <BigButton glyph="phone" label="Call" disabled={!phoneNumber}
            href={phoneNumber ? `tel:${phoneNumber.replace(/[^\d+]/g, '')}` : undefined}
            onClick={() => call.called()} />
          <BigButton glyph="bubble" label="Text" disabled={!canText}
            onClick={() => openThread(contact?.prefers === 'email' || !phoneNumber ? 'email' : 'sms')} />
        </div>

        {target && (
          <Section title="Scope it" right={targetLabel && (
            <span className="text-[11.5px] font-medium normal-case tracking-normal text-text-muted">saves to {targetLabel}</span>
          )}>
            <div className="flex gap-1.5">
              <ScopeTile glyph="mic" label="Voice note" sub="typed for you" onClick={() => setScreen('voice')} />
              <ScopeTile glyph="camera" label="Photo" sub="or several" onClick={() => photoInput.current?.click()} />
              <ScopeTile glyph="video" label="Video" sub="up to 5 min" onClick={() => videoInput.current?.click()} />
              <ScopeTile glyph="pencil" label="Note" sub="type it" onClick={() => setScreen('note')} />
            </div>
            {scope.problem && screen !== 'photos' && (
              <p role="alert" className="mt-2 text-[12.5px] font-semibold text-danger">{scope.problem}</p>
            )}
            <input ref={photoInput} type="file" accept="image/*" capture="environment" multiple className="hidden"
              onChange={(event) => void pick(event.target.files, 'photo')} />
            <input ref={videoInput} type="file" accept="video/*" capture="environment" className="hidden"
              onChange={(event) => void pick(event.target.files, 'video')} />
          </Section>
        )}

        {card.job && card.wants && (
          <Section title="What they want">
            <div className="text-[13.5px] leading-[1.5] text-text-body">{card.wants}</div>
          </Section>
        )}

        {contact && (
          <Section title={contact.name}>
            <div className="flex items-center gap-2.5">
              <Avatar name={contact.name} size={34} className="!text-[11.6px] tracking-[.02em]" />
              <div className="flex min-w-0 flex-col">
                <b className="text-[14px]">{phoneNumber ? phoneWords(phoneNumber) : contact.email}</b>
                {contactLine && <span className="text-[12px] text-text-muted">{contactLine}</span>}
              </div>
            </div>
          </Section>
        )}

        {card.latest && (
          <Section title="Latest" right={(
            <button type="button" onClick={() => openThread(card.latest!.channel)}
              className="text-[12px] font-semibold normal-case tracking-normal text-accent">All</button>
          )}>
            <div className="flex items-start gap-2 text-[13px]">
              <span className="mt-0.5 inline-flex" style={{ color: LATEST_TEAL }}>
                <Glyph name={card.latest.channel === 'email' ? 'envelope' : 'bubble'} size={14} stroke={2.2} />
              </span>
              <div className="flex min-w-0 flex-col">
                <span><b>{card.latest.who}</b> · {timeOf(card.latest.at)}</span>
                <span className="block max-w-[320px] truncate text-text-muted">{card.latest.preview}</span>
              </div>
            </div>
          </Section>
        )}
      </div>

      <div className="flex shrink-0 gap-2 border-t border-line bg-surface px-3.5 pt-2.5 pb-[max(16px,env(safe-area-inset-bottom))]">
        {card.job && (
          /* Over the stop page, not instead of it: back from the job comes back here (Aaron, Oct 26). */
          <button type="button" onClick={() => navigate(`/jobs/${card.job!.id}`)}
            className="inline-flex h-[46px] w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-line bg-surface px-3.5 text-[14px] font-semibold text-text">
            <Glyph name="layers" size={16} />Open {card.job.reference}
          </button>
        )}
        <button type="button" disabled={busy || card.done || !offersDone(card)} onClick={() => void done()}
          className="inline-flex h-[46px] w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-accent bg-accent px-3.5 text-[14px] font-semibold text-white disabled:opacity-60">
          <Glyph name="check" size={16} />Done
        </button>
      </div>

      {sheet === 'navigate' && (
        <NavigateSheet street={street} place={placeLine} chosen={knownApp}
          onClose={() => setSheet(null)}
          onChoose={(app, always) => {
            if (always) { nav.remember(app); setKnownApp(app) }
            setSheet(null)
            openNav(app, destination, true)
          }} />
      )}
      {sheet === 'more' && (
        <StopSheet title={card.title} sub={`${weekdayLong(card.date)} · ${clockRange(card.start, card.end)}`}
          onClose={() => setSheet(null)}>
          {/* The calendar menu's order (`itemMenu`): change it, then tell the customer, then its place. */}
          {card.editable && <MoreRow glyph="pencil" label="Edit" onClick={() => change('edit')} />}
          {card.editable && <MoreRow glyph="calendar" label="Reschedule" onClick={() => change('reschedule')} />}
          {contact?.email && (
            <MoreRow glyph="envelope" label={`Email ${contact.firstName}`} onClick={() => { setSheet(null); openThread('email') }} />
          )}
          {card.propertyId && (
            <MoreRow glyph="pin" label={`Open ${card.propertyName ?? 'the property'}`}
              onClick={() => { setSheet(null); navigate(`/properties/${card.propertyId}`) }} />
          )}
        </StopSheet>
      )}

      {sheet === 'which' && (
        <WhichSheet title={card.title} verb={asking} date={card.date} onClose={() => setSheet(null)}
          onPick={(which) => {
            if (which === 'all') openOwnSheet()
            else if (asking === 'edit') openOwnSheet(card.occurrence ?? card.date)
            else setSheet('move')
          }} />
      )}
      {sheet === 'move' && (
        <MoveSheet item={{ title: card.title, date: card.date, start: card.start, end: card.end, occurrence: card.occurrence ?? null }}
          mode="one" onClose={() => setSheet(null)} onSave={moveOne} />
      )}

      {screen === 'voice' && (
        <VoiceNoteSheet links={voiceLinks} madeFromActivityId={card.id} onClose={() => setScreen(null)} />
      )}
      {screen === 'photos' && (
        <PhotosScreen scope={scope} chips={chips}
          onTakeMore={() => photoInput.current?.click()}
          onClose={() => setScreen(null)} />
      )}
      {screen === 'note' && (
        <NoteSheet refs={refs} chips={chips} madeFromActivityId={card.id} batch={scope.batch}
          onClose={() => setScreen(null)} />
      )}
      {call.back && contact && (
        <LogCallSheet
          name={contact.name}
          call={call.back}
          refs={[...(card.job ? [{ type: 'job' as const, id: card.job.id }] : []), { type: 'contact', id: contact.id }]}
          chips={[
            ...(card.job ? [{ type: 'job' as const, id: card.job.id, label: card.job.reference }] : []),
            { type: 'contact', id: contact.id, label: contact.name },
          ]}
          madeFromActivityId={card.id}
          onClose={call.clear}
        />
      )}
    </div>,
    document.body,
  )
}

function MoreRow({ glyph, label, onClick }: { glyph: GlyphName; label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className="flex h-[52px] w-full items-center gap-3 border-b border-line-faint text-left text-[15px] font-semibold">
      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-line-soft text-text">
        <Glyph name={glyph} size={17} />
      </span>
      {label}
    </button>
  )
}
